import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";
import { reviewAgentAiSettings } from "../../api/client";
import { translateSkillForm } from "../../utils/skillLifecycle";

const MODEL_OPTIONS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini (rápido e econômico)" },
  { value: "gpt-4.1", label: "GPT-4.1" },
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
];

const severityColor = (severity) => {
  if (severity === "critical") return "error";
  if (severity === "warning") return "warning";
  return "info";
};

export default function AgentAiSettingsPanel({
  model,
  setModel,
  temperature,
  setTemperature,
  systemPrompt,
  setSystemPrompt,
  readOnly,
  skillKey = null,
  skillConfig = {},
  agentName = "",
  sourceUrls = [],
  documents = [],
  knowledgePreviews = {},
}) {
  const { t } = useTranslation("settings");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);

  const canAnalyze = Boolean(skillKey) && !readOnly;

  const openReview = async () => {
    if (!skillKey) {
      setDialogOpen(true);
      setError(t("agentsListPage.formAiReviewNeedSkill"));
      setReview(null);
      return;
    }
    setDialogOpen(true);
    setLoading(true);
    setError(null);
    setReview(null);
    try {
      const previewList = Object.values(knowledgePreviews || {}).filter(Boolean);
      const res = await reviewAgentAiSettings({
        skill_key: skillKey,
        skill_config: skillConfig && typeof skillConfig === "object" ? skillConfig : {},
        model,
        temperature,
        system_prompt: systemPrompt,
        agent_name: agentName || undefined,
        source_urls: Array.isArray(sourceUrls) ? sourceUrls.filter(Boolean) : [],
        documents: (documents || [])
          .map((d) => ({
            file_url: d?.url || d?.file_url,
            file_name: d?.name || d?.file_name || "",
          }))
          .filter((d) => d.file_url),
        knowledge_previews: previewList.length ? previewList : undefined,
      });
      const data = res?.data ?? res ?? null;
      setReview(data);
    } catch (err) {
      setError(err?.message || t("agentsListPage.formAiReviewError"));
    } finally {
      setLoading(false);
    }
  };

  const applySuggestedPrompt = () => {
    if (!review?.suggested_prompt || readOnly) return;
    setSystemPrompt(review.suggested_prompt);
  };

  const applySuggestedTemperature = () => {
    if (review?.suggested_temperature == null || readOnly) return;
    const n = Number(review.suggested_temperature);
    if (Number.isFinite(n)) setTemperature(n);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {t("agentsListPage.formAiReviewHint")}
        </Typography>
        <Tooltip
          title={
            canAnalyze
              ? t("agentsListPage.formAiReviewTooltip")
              : readOnly
                ? t("agentsListPage.formAiReviewTooltipReadonly")
                : t("agentsListPage.formAiReviewNeedSkill")
          }
        >
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={openReview}
              disabled={readOnly || loading}
              aria-label={t("agentsListPage.formAiReviewTooltip")}
            >
              {loading ? <CircularProgress size={18} /> : <AutoAwesomeIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <FormControl fullWidth size="small" disabled={readOnly}>
        <InputLabel>{t("agentsListPage.formAiModelLabel")}</InputLabel>
        <Select
          label={t("agentsListPage.formAiModelLabel")}
          value={model}
          onChange={(e) => setModel(e.target.value)}
        >
          {MODEL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box>
        <Typography gutterBottom variant="body2">
          {t("agentsListPage.formAiTemperatureLabel")}
        </Typography>
        <Slider
          value={temperature}
          onChange={(_, v) => setTemperature(v)}
          min={0}
          max={2}
          step={0.1}
          valueLabelDisplay="auto"
          disabled={readOnly}
          marks={[
            { value: 0, label: "0" },
            { value: 1, label: "1" },
            { value: 2, label: "2" },
          ]}
        />
        <Typography variant="caption" color="text.secondary">
          {t("agentsListPage.formAiTemperatureHelper")}
        </Typography>
      </Box>
      <TextField
        label={t("agentsListPage.formAiPromptLabel")}
        value={systemPrompt}
        onChange={(e) => setSystemPrompt(e.target.value)}
        multiline
        rows={4}
        fullWidth
        size="small"
        placeholder={translateSkillForm(
          t,
          skillKey,
          "formAiPromptPlaceholder",
          "agentsListPage.formAiPromptPlaceholder"
        )}
        helperText={translateSkillForm(
          t,
          skillKey,
          "formAiPromptHelper",
          "agentsListPage.formAiPromptHelper"
        )}
        inputProps={{ readOnly }}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5 }}>
        {translateSkillForm(
          t,
          skillKey,
          "formAiAwarenessNote",
          "agentsListPage.formAiAwarenessNote"
        )}
      </Typography>

      <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          {t("agentsListPage.formAiReviewDialogTitle")}
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Stack spacing={1.5} alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                {t("agentsListPage.formAiReviewLoading")}
              </Typography>
            </Stack>
          )}
          {!loading && error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && review && (
            <Stack spacing={2}>
              {review.playbook?.label && (
                <Chip
                  size="small"
                  label={t("agentsListPage.formAiReviewSkillChip", { skill: review.playbook.label })}
                  color="primary"
                  variant="outlined"
                  sx={{ alignSelf: "flex-start" }}
                />
              )}
              {review.summary && (
                <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                  {review.summary}
                </Typography>
              )}
              {review.skill_context && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("agentsListPage.formAiReviewSkillContext")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {review.skill_context}
                  </Typography>
                </Box>
              )}

              {Array.isArray(review.findings) && review.findings.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("agentsListPage.formAiReviewFindings")}
                  </Typography>
                  <List dense disablePadding>
                    {review.findings.map((f, idx) => (
                      <ListItem key={`${f.title}-${idx}`} alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={f.severity || "info"} color={severityColor(f.severity)} />
                              <Typography variant="body2" fontWeight={600}>
                                {f.title}
                              </Typography>
                            </Stack>
                          }
                          secondary={f.detail}
                          secondaryTypographyProps={{ variant: "body2", sx: { mt: 0.5 } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {(review.model_advice ||
                review.temperature_advice ||
                review.prompt_advice ||
                review.knowledge_advice) && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("agentsListPage.formAiReviewAdvice")}
                  </Typography>
                  <Stack spacing={1}>
                    {review.model_advice && (
                      <Typography variant="body2">
                        <strong>{t("agentsListPage.formAiModelLabel")}:</strong> {review.model_advice}
                      </Typography>
                    )}
                    {review.temperature_advice && (
                      <Typography variant="body2">
                        <strong>{t("agentsListPage.formAiTemperatureLabel")}:</strong>{" "}
                        {review.temperature_advice}
                      </Typography>
                    )}
                    {review.prompt_advice && (
                      <Typography variant="body2">
                        <strong>{t("agentsListPage.formAiPromptLabel")}:</strong> {review.prompt_advice}
                      </Typography>
                    )}
                    {review.knowledge_advice && (
                      <Typography variant="body2">
                        <strong>{t("agentsListPage.formAiReviewKnowledgeLabel")}:</strong>{" "}
                        {review.knowledge_advice}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {Array.isArray(review.knowledge_sources) && review.knowledge_sources.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("agentsListPage.formAiReviewKnowledgeSources")}
                  </Typography>
                  <List dense disablePadding>
                    {review.knowledge_sources.map((src, idx) => (
                      <ListItem key={`${src.label}-${idx}`} sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                size="small"
                                label={src.quality || "info"}
                                color={
                                  src.quality === "ok"
                                    ? "success"
                                    : src.quality === "low"
                                      ? "warning"
                                      : src.quality === "empty"
                                        ? "error"
                                        : "default"
                                }
                              />
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {src.label}
                              </Typography>
                            </Stack>
                          }
                          secondary={src.reason || (src.wordCount != null ? `${src.wordCount} palavras` : null)}
                          secondaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {Array.isArray(review.config_tips) && review.config_tips.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("agentsListPage.formAiReviewConfigTips")}
                  </Typography>
                  <List dense disablePadding>
                    {review.config_tips.map((tip, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.25 }}>
                        <ListItemText primary={tip} primaryTypographyProps={{ variant: "body2" }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {review.suggested_prompt && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("agentsListPage.formAiReviewSuggestedPrompt")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        p: 1.5,
                        lineHeight: 1.45,
                      }}
                    >
                      {review.suggested_prompt}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, flexWrap: "wrap", gap: 1 }}>
          {!readOnly && review?.suggested_temperature != null && (
            <Button size="small" onClick={applySuggestedTemperature} disabled={loading}>
              {t("agentsListPage.formAiReviewApplyTemperature", {
                value: review.suggested_temperature,
              })}
            </Button>
          )}
          {!readOnly && review?.suggested_prompt && (
            <Button size="small" variant="contained" onClick={applySuggestedPrompt} disabled={loading}>
              {t("agentsListPage.formAiReviewApplyPrompt")}
            </Button>
          )}
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>
            {t("agentsListPage.formAiReviewClose")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
